import { useEffect, useMemo, useState } from "react";
import client from "../api/client";
import { getNextVoucherNo } from "../services/voucherNumberService";

const today = () => new Date().toISOString().split("T")[0];

const REFERENCE_TYPES = [
  {
    value: "ON_ACCOUNT",
    label: "On A/C",
    help: "General payment without bill adjustment",
  },
  {
    value: "TOWARDS_BILL",
    label: "Against Bill / Reference",
    help: "Supplier payment against bill/reference",
  },
  {
    value: "ADVANCE",
    label: "Advance",
    help: "Advance paid before purchase bill",
  },
  {
    value: "NEW_BILL",
    label: "New Bill",
    help: "Payment linked to a new manual bill reference",
  },
];

const initialForm = {
  voucherNo: "",
  voucherDate: today(),
  paymentMode: "CASH",
  referenceType: "ON_ACCOUNT",
  billReferenceNo: "",
  supplierId: "",
  debitAccountId: "",
  creditAccountId: "",
  amount: "",
  notes: "",
};

const getId = (x) => x?.Id ?? x?.id ?? x?.AccountId ?? x?.accountId ?? "";
const getAccountCode = (x) => x?.AccountCode ?? x?.accountCode ?? "";
const getAccountName = (x) => x?.AccountName ?? x?.accountName ?? "";
const getAccountType = (x) =>
  (x?.AccountType ?? x?.accountType ?? "").toString();
const getNormal = (x) =>
  (
    x?.NormalBalance ??
    x?.normalBalance ??
    x?.NormalSide ??
    x?.normalSide ??
    x?.Normal ??
    x?.normal ??
    ""
  ).toString();
const isGstLedger = (x) =>
  /gst|cgst|sgst|igst/i.test(`${getAccountCode(x)} ${getAccountName(x)}`);
const isCashBankLedger = (x) => {
  const name = getAccountName(x).toLowerCase();
  const code = getAccountCode(x);
  return (
    name.includes("cash") ||
    name.includes("bank") ||
    name.includes("upi") ||
    code === "1100" ||
    code === "1200"
  );
};
const isPayableLedger = (x) => {
  const name = getAccountName(x).toLowerCase();
  const code = getAccountCode(x);
  return (
    code === "2100" ||
    name.includes("payable") ||
    name.includes("creditor") ||
    name.includes("sundry creditor") ||
    name.includes("supplier")
  );
};
const isExpenseLedger = (x) => {
  const name = getAccountName(x).toLowerCase();
  const type = getAccountType(x).toLowerCase();
  return (
    type === "expense" ||
    name.includes("expense") ||
    name.includes("rent") ||
    name.includes("salary") ||
    name.includes("electricity") ||
    name.includes("wages")
  );
};
const isPurchaseLedger = (x) => {
  const name = getAccountName(x).toLowerCase();
  return name.includes("purchase") || name.includes("inventory");
};
const isLiabilityLedger = (x) => {
  const name = getAccountName(x).toLowerCase();
  const type = getAccountType(x).toLowerCase();
  return (
    type === "liability" ||
    name.includes("loan") ||
    name.includes("payable") ||
    name.includes("gst payable") ||
    name.includes("advance")
  );
};
const getSupplierId = (x) =>
  x?.Id ?? x?.id ?? x?.SupplierId ?? x?.supplierId ?? "";
const getSupplierName = (x) =>
  x?.SupplierName ?? x?.supplierName ?? x?.Name ?? x?.name ?? "";
const getSupplierCode = (x) =>
  x?.SupplierCode ?? x?.supplierCode ?? x?.Code ?? x?.code ?? "";

const money = (v) =>
  Number(v || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

export default function PaymentVoucherPage() {
  const [form, setForm] = useState(initialForm);
  const [accounts, setAccounts] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [saving, setSaving] = useState(false);
  const [autoNarration, setAutoNarration] = useState(true);

  const findAccount = (mode, list = accounts) => {
    const m = (mode || "").toLowerCase();
    if (m === "cash")
      return list.find(
        (x) =>
          getAccountName(x).toLowerCase().includes("cash") ||
          getAccountCode(x) === "1100",
      );
    if (m === "bank")
      return list.find(
        (x) =>
          getAccountName(x).toLowerCase().includes("bank") ||
          getAccountCode(x) === "1200",
      );
    if (m === "upi")
      return (
        list.find((x) => getAccountName(x).toLowerCase().includes("upi")) ||
        list.find((x) => getAccountName(x).toLowerCase().includes("bank"))
      );
    return list.find((x) => getAccountType(x).toLowerCase() === "asset");
  };

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    const shortcut = (e) => {
      if (e.ctrlKey && e.key.toLowerCase() === "s") {
        e.preventDefault();
        save();
      }
      if (e.altKey && e.key.toLowerCase() === "c") {
        e.preventDefault();
        clearForm();
      }
    };
    window.addEventListener("keydown", shortcut);
    return () => window.removeEventListener("keydown", shortcut);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form, accounts]);

  const accountMap = useMemo(() => {
    const map = {};
    accounts.forEach((a) => {
      map[String(getId(a))] = `${getAccountCode(a)} - ${getAccountName(a)}`;
    });
    return map;
  }, [accounts]);

  const paymentDebitAccounts = useMemo(
    () =>
      accounts.filter((a) => {
        // Production filter: supplier bill payment should show payable/supplier only;
        // expense payment should show expense ledgers; tax/loan payment should show liabilities.
        if (isCashBankLedger(a)) return false;
        if (form.referenceType === "TOWARDS_BILL") return isPayableLedger(a);
        if (form.referenceType === "ADVANCE")
          return isPayableLedger(a) || isLiabilityLedger(a);
        if (isGstLedger(a))
          return getAccountName(a).toLowerCase().includes("payable");
        return (
          isPayableLedger(a) ||
          isExpenseLedger(a) ||
          isPurchaseLedger(a) ||
          isLiabilityLedger(a)
        );
      }),
    [accounts, form.referenceType],
  );

  const selectedSupplier = useMemo(
    () =>
      suppliers.find(
        (s) => String(getSupplierId(s)) === String(form.supplierId),
      ),
    [suppliers, form.supplierId],
  );

  const recommendedDebitAccountId = useMemo(() => {
    const exactSupplier = selectedSupplier
      ? paymentDebitAccounts.find((a) =>
          getAccountName(a)
            .toLowerCase()
            .includes(getSupplierName(selectedSupplier).toLowerCase()),
        )
      : null;
    const payable = paymentDebitAccounts.find(
      (a) =>
        getAccountCode(a) === "2100" ||
        getAccountName(a).toLowerCase().includes("payable"),
    );
    const expense = paymentDebitAccounts.find(
      (a) =>
        getAccountType(a).toLowerCase() === "expense" ||
        getAccountName(a).toLowerCase().includes("expense"),
    );
    return (
      getId(exactSupplier || payable || expense || paymentDebitAccounts[0]) ||
      ""
    );
  }, [paymentDebitAccounts, selectedSupplier]);

  const paymentCreditAccounts = useMemo(
    () =>
      accounts.filter((a) => {
        const name = getAccountName(a).toLowerCase();
        const code = getAccountCode(a);
        const type = getAccountType(a).toLowerCase();
        return isCashBankLedger(a);
      }),
    [accounts],
  );

  useEffect(() => {
    if (
      !form.debitAccountId ||
      !paymentDebitAccounts.some(
        (a) => String(getId(a)) === String(form.debitAccountId),
      )
    ) {
      if (recommendedDebitAccountId)
        setForm((prev) => ({
          ...prev,
          debitAccountId: recommendedDebitAccountId,
        }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.referenceType, form.supplierId, recommendedDebitAccountId]);

  const selectedDebitHint =
    form.referenceType === "TOWARDS_BILL"
      ? "Select 2100 - Accounts Payable A/c or exact Supplier ledger. Do not select Cash/Bank here."
      : form.referenceType === "ADVANCE"
        ? "Select Supplier Advance / Liability or Supplier ledger."
        : "Select Supplier / Expense / Liability ledger. Cash/Bank ledgers are hidden.";

  const debitName =
    accountMap[String(form.debitAccountId)] || "Supplier / Expense A/c";
  const creditName =
    accountMap[String(form.creditAccountId)] || "Cash / Bank A/c";

  const generateVoucherNo = () => localFallbackVoucherNo("PAY");

  const localFallbackVoucherNo = (prefix) => {
    const d = new Date();
    return `${prefix}-${d.getFullYear()}${String(d.getMonth() + 1).padStart(2, "0")}${String(d.getDate()).padStart(2, "0")}-${String(d.getHours()).padStart(2, "0")}${String(d.getMinutes()).padStart(2, "0")}${String(d.getSeconds()).padStart(2, "0")}`;
  };

  const refreshVoucherNo = async () => {
    const no = await getNextVoucherNo("payment", form.voucherDate);
    setForm((prev) => ({ ...prev, voucherNo: no }));
    return no;
  };

  const buildNarration = (f = form) => {
    const refLabel =
      REFERENCE_TYPES.find((x) => x.value === f.referenceType)?.label ||
      "On A/C";
    const party = selectedSupplier
      ? getSupplierName(selectedSupplier)
      : "party";
    const ref = f.billReferenceNo?.trim()
      ? `, Ref: ${f.billReferenceNo.trim()}`
      : "";
    return `Being amount paid to ${party} by ${f.paymentMode}${ref} (${refLabel})`;
  };

  const loadData = async () => {
    try {
      const [accRes, supRes] = await Promise.all([
        client.get("/accounts"),
        client.get("/suppliers"),
      ]);
      const acc = Array.isArray(accRes.data) ? accRes.data : [];
      const sup = Array.isArray(supRes.data) ? supRes.data : [];
      const cash = findAccount("CASH", acc);

      setAccounts(acc);
      setSuppliers(sup);
      const nextNo = await getNextVoucherNo("payment", today());
      setForm((prev) => ({
        ...prev,
        voucherNo: prev.voucherNo || nextNo,
        creditAccountId: getId(cash) || "",
      }));
    } catch (err) {
      console.error(err);
      alert("Failed to load accounts/suppliers");
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === "paymentMode") {
      const selectedAccount = findAccount(value);
      setForm((prev) => ({
        ...prev,
        paymentMode: value,
        creditAccountId: getId(selectedAccount) || "",
        notes: autoNarration ? "" : prev.notes,
      }));
      return;
    }

    if (name === "referenceType") {
      setForm((prev) => ({
        ...prev,
        referenceType: value,
        billReferenceNo:
          value === "ON_ACCOUNT" || value === "ADVANCE"
            ? ""
            : prev.billReferenceNo,
        notes: autoNarration ? "" : prev.notes,
      }));
      return;
    }

    setForm((prev) => ({
      ...prev,
      [name]: value,
      notes:
        autoNarration && name !== "notes"
          ? ""
          : name === "notes"
            ? value
            : prev.notes,
    }));
  };

  const clearForm = async () => {
    const defaultAccount = findAccount(form.paymentMode);
    const nextNo = await getNextVoucherNo("payment", today());
    setForm({
      ...initialForm,
      voucherNo: nextNo,
      voucherDate: today(),
      paymentMode: form.paymentMode,
      creditAccountId: getId(defaultAccount) || "",
    });
  };

  const validationErrors = useMemo(() => {
    const errors = [];
    if (!form.voucherNo.trim()) errors.push("Voucher No is required");
    if (!form.debitAccountId) errors.push("Select Debit Account");
    if (!form.creditAccountId) errors.push("Select Credit Account");
    if (
      String(form.debitAccountId) === String(form.creditAccountId) &&
      form.debitAccountId
    )
      errors.push("Debit and Credit account cannot be same");
    if (
      (form.referenceType === "NEW_BILL" ||
        form.referenceType === "TOWARDS_BILL") &&
      !form.billReferenceNo.trim()
    )
      errors.push("Bill / Reference No is required");
    if (!form.amount || Number(form.amount) <= 0)
      errors.push("Enter valid amount");
    return errors;
  }, [form]);

  const save = async () => {
    if (validationErrors.length) return alert(validationErrors[0]);

    try {
      setSaving(true);
      await client.post("/vouchers/payment", {
        companyId: Number(localStorage.getItem("companyId") || 0),
        voucherNo: form.voucherNo.trim(),
        voucherDate: form.voucherDate,
        paymentMode: form.paymentMode,
        referenceType: form.referenceType,
        billReferenceNo: form.billReferenceNo.trim() || null,
        supplierId: form.supplierId ? Number(form.supplierId) : null,
        debitAccountId: Number(form.debitAccountId),
        creditAccountId: Number(form.creditAccountId),
        amount: Number(form.amount),
        notes: (form.notes || buildNarration()).trim(),
      });

      alert("Payment saved and journal posted");
      await clearForm();
    } catch (err) {
      console.error("Payment Save Error:", err);
      const msg =
        err?.response?.data?.message ||
        err?.response?.data?.title ||
        err?.response?.data ||
        err?.message ||
        "Failed to save payment";
      alert(msg);
    } finally {
      setSaving(false);
    }
  };

  const labelStyle = {
    fontSize: "12px",
    fontWeight: "600",
    marginBottom: "3px",
  };
  const inputStyle = { height: "30px", fontSize: "12px", borderRadius: "6px" };
  const buttonStyle = {
    border: "none",
    padding: "5px 14px",
    fontSize: "12px",
    borderRadius: "6px",
  };
  const effectiveNotes = form.notes || buildNarration();

  return (
    <div style={{ padding: "0px", fontSize: "13px" }}>
      <div
        style={{
          background: "#fff",
          borderRadius: "10px",
          boxShadow: "0 3px 10px rgba(0,0,0,0.08)",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            background: "linear-gradient(90deg,#dc3545,#fd7e14)",
            color: "#fff",
            padding: "10px 14px",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <div>
            <div style={{ fontSize: "15px", fontWeight: "700" }}>
              Payment Voucher
            </div>
            <div style={{ fontSize: "11px", opacity: 0.9 }}>
              Supplier/Expense/Liability Dr → Cash/Bank Cr | Ctrl + S Save | Alt
              + C Clear
            </div>
          </div>
          <span
            style={{
              background: "#fff",
              color: "#dc3545",
              padding: "3px 8px",
              borderRadius: "6px",
              fontSize: "11px",
              fontWeight: "600",
            }}
          >
            Auto Journal + Locked Dr/Cr
          </span>
        </div>

        <div style={{ padding: "12px" }}>
          <div className="row g-2">
            <div className="col-md-3">
              <label style={labelStyle}>Voucher No</label>
              <input
                name="voucherNo"
                className="form-control"
                style={inputStyle}
                value={form.voucherNo}
                readOnly
              />
              <button
                type="button"
                className="btn btn-outline-secondary btn-sm mt-1"
                style={{ fontSize: "11px", padding: "2px 8px" }}
                onClick={refreshVoucherNo}
              >
                Refresh No
              </button>
            </div>
            <div className="col-md-3">
              <label style={labelStyle}>Date</label>
              <input
                type="date"
                name="voucherDate"
                className="form-control"
                style={inputStyle}
                value={form.voucherDate}
                onChange={handleChange}
              />
            </div>
            <div className="col-md-3">
              <label style={labelStyle}>Payment Mode</label>
              <select
                name="paymentMode"
                className="form-select"
                style={inputStyle}
                value={form.paymentMode}
                onChange={handleChange}
              >
                <option value="CASH">Cash</option>
                <option value="BANK">Bank</option>
                <option value="UPI">UPI</option>
              </select>
            </div>
            <div className="col-md-3">
              <label style={labelStyle}>Reference Type</label>
              <select
                name="referenceType"
                className="form-select"
                style={inputStyle}
                value={form.referenceType}
                onChange={handleChange}
              >
                {REFERENCE_TYPES.map((x) => (
                  <option key={x.value} value={x.value}>
                    {x.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-md-3">
              <label style={labelStyle}>Bill / Reference No</label>
              <input
                name="billReferenceNo"
                className="form-control"
                style={inputStyle}
                value={form.billReferenceNo}
                onChange={handleChange}
                disabled={
                  form.referenceType === "ON_ACCOUNT" ||
                  form.referenceType === "ADVANCE"
                }
                placeholder={
                  form.referenceType === "TOWARDS_BILL"
                    ? "Bill / reference no"
                    : form.referenceType === "NEW_BILL"
                      ? "New bill no"
                      : "Not required"
                }
              />
            </div>
            <div className="col-md-3">
              <label style={labelStyle}>Supplier</label>
              <select
                name="supplierId"
                className="form-select"
                style={inputStyle}
                value={form.supplierId}
                onChange={handleChange}
              >
                <option value="">Optional - Select Supplier</option>
                {suppliers.map((s) => (
                  <option key={getSupplierId(s)} value={getSupplierId(s)}>
                    {getSupplierCode(s)} - {getSupplierName(s)}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-md-3">
              <label style={labelStyle}>
                Debit Account - Supplier/Expense/Dr
              </label>
              <select
                name="debitAccountId"
                className="form-select"
                style={inputStyle}
                value={form.debitAccountId}
                onChange={handleChange}
              >
                <option value="">Select Supplier / Payable / Expense</option>
                {paymentDebitAccounts.map((a) => (
                  <option key={getId(a)} value={getId(a)}>
                    {getAccountCode(a)} - {getAccountName(a)}
                  </option>
                ))}
              </select>
              <div
                style={{ fontSize: "10px", color: "#dc3545", marginTop: "3px" }}
              >
                {selectedDebitHint}
              </div>
            </div>
            <div className="col-md-3">
              <label style={labelStyle}>Credit Account - Cash/Bank/Asset</label>
              <select
                name="creditAccountId"
                className="form-select"
                style={inputStyle}
                value={form.creditAccountId}
                onChange={handleChange}
              >
                <option value="">Select Cash / Bank / UPI</option>
                {(paymentCreditAccounts.length
                  ? paymentCreditAccounts
                  : accounts
                ).map((a) => (
                  <option key={getId(a)} value={getId(a)}>
                    {getAccountCode(a)} - {getAccountName(a)}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-md-3">
              <label style={labelStyle}>Amount</label>
              <input
                type="number"
                name="amount"
                className="form-control"
                style={{ ...inputStyle, textAlign: "right" }}
                value={form.amount}
                onChange={handleChange}
              />
            </div>
            <div className="col-md-9">
              <label style={labelStyle}>Notes</label>
              <input
                name="notes"
                className="form-control"
                style={inputStyle}
                value={effectiveNotes}
                onChange={(e) => {
                  setAutoNarration(false);
                  setForm((p) => ({ ...p, notes: e.target.value }));
                }}
                placeholder="Example: Amount received against invoice / Paid supplier bill / Advance adjusted"
              />
            </div>
          </div>

          <div
            style={{
              marginTop: 8,
              display: "flex",
              gap: 14,
              alignItems: "center",
              fontSize: 11,
            }}
          >
            <label style={{ display: "flex", alignItems: "center", gap: 5 }}>
              <input
                type="checkbox"
                checked={autoNarration}
                onChange={(e) => {
                  setAutoNarration(e.target.checked);
                  if (e.target.checked) setForm((p) => ({ ...p, notes: "" }));
                }}
              />{" "}
              Auto narration
            </label>
            <span style={{ color: "#dc3545" }}>
              {
                REFERENCE_TYPES.find((x) => x.value === form.referenceType)
                  ?.help
              }
            </span>
          </div>

          <div
            style={{
              marginTop: "12px",
              padding: "10px",
              border: "1px dashed #f1c5bb",
              background: "#fffaf8",
              borderRadius: "8px",
              fontSize: "12px",
            }}
          >
            <div style={{ fontWeight: "700", marginBottom: "5px" }}>
              Journal Preview
            </div>
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <span>{debitName} Dr</span>
              <b>₹ {money(form.amount)}</b>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <span>To {creditName}</span>
              <b>₹ {money(form.amount)}</b>
            </div>
            <div style={{ marginTop: 5, color: "#dc3545", fontSize: 11 }}>
              {effectiveNotes}
            </div>
          </div>

          {validationErrors.length > 0 && (
            <div style={{ marginTop: 8, color: "#dc3545", fontSize: 11 }}>
              {validationErrors[0]}
            </div>
          )}
          <div style={{ marginTop: "12px", display: "flex", gap: "8px" }}>
            <button
              onClick={save}
              disabled={saving || validationErrors.length > 0}
              style={{
                ...buttonStyle,
                background:
                  saving || validationErrors.length > 0 ? "#dda69c" : "#dc3545",
                color: "#fff",
              }}
            >
              {saving ? "Saving..." : "Save"}
            </button>
            <button
              onClick={clearForm}
              type="button"
              style={{ ...buttonStyle, background: "#6c757d", color: "#fff" }}
            >
              Clear
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
